import { BACKEND_URL } from '@/config';
﻿import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/context/ToastContext';
import axios from 'axios';
import { AlertTriangle, AlertCircle, ImageIcon, X, ShieldCheck, ShieldX, Eye } from 'lucide-react';
import { ccySym } from '@/utils/currencyUtils';
import ConfirmDialog from '@/components/ConfirmDialog';

const API = BACKEND_URL + '/api';
axios.defaults.withCredentials = true;

const CATEGORY_LABELS = {
  no_show:    { l: 'Transporteur absent',   tone: 'red' },
  damage:     { l: 'Colis endommagé',       tone: 'red' },
  lost:       { l: 'Colis perdu',           tone: 'red' },
  wrong_item: { l: 'Mauvais colis livré',   tone: 'gold' },
  late:       { l: 'Retard important',      tone: 'gold' },
  no_payment: { l: 'Problème de paiement',  tone: 'red' },
  fraud:      { l: 'Fraude suspectée',      tone: 'red' },
  other:      { l: 'Autre',                 tone: 'gray' },
};

const fmtDateTime = (d) => d ? new Date(d).toLocaleString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '·';

/* ─── Photo Lightbox ─── */
function PhotoLightbox({ photos, index, onClose, onPrev, onNext }) {
  if (index == null || !photos[index]) return null;
  return (
    <div className="modal-backdrop" style={{ background: 'rgba(0,0,0,.85)' }} onClick={onClose}>
      <button className="modal-close" style={{ position: 'fixed', top: 20, right: 20, color: '#fff', width: 40, height: 40, fontSize: 22 }} onClick={onClose}><X size={22} /></button>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, maxWidth: '90vw' }} onClick={e => e.stopPropagation()}>
        {photos.length > 1 && <button onClick={onPrev} style={{ background: 'rgba(255,255,255,.15)', border: 'none', color: '#fff', width: 40, height: 40, borderRadius: '50%', cursor: 'pointer' }}>‹</button>}
        <img src={photos[index]} alt="preuve" style={{ maxWidth: 'calc(90vw - 100px)', maxHeight: '85vh', borderRadius: 12 }} />
        {photos.length > 1 && <button onClick={onNext} style={{ background: 'rgba(255,255,255,.15)', border: 'none', color: '#fff', width: 40, height: 40, borderRadius: '50%', cursor: 'pointer' }}>›</button>}
      </div>
    </div>
  );
}

export default function AdminDisputes() {
  const navigate = useNavigate();
  const toast = useToast();
  const [disputes, setDisputes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState('');
  const [confirmDlg, setConfirmDlg] = useState(null); // { decision: 'refund'|'release', id }
  const [lightbox, setLightbox] = useState({ photos: [], index: null });

  const load = () => {
    setLoading(true);
    axios.get(`${API}/admin/disputes`)
      .then(r => setDisputes(r.data || []))
      .catch(() => toast.error('Erreur de chargement'))
      .finally(() => setLoading(false));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const doResolve = async () => {
    const { id, decision } = confirmDlg;
    const labels = { refund: "Rembourser l'expéditeur", release: 'Libérer les fonds au transporteur' };
    setConfirmDlg(null);
    setActing(`${id}-${decision}`);
    try {
      await axios.patch(`${API}/admin/disputes/${id}/resolve`, { decision });
      toast.success(`Litige résolu : ${labels[decision]}.`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur');
    } finally { setActing(''); }
  };

  const openLightbox = (photos, i) => setLightbox({ photos, index: i });
  const closeLightbox = () => setLightbox({ photos: [], index: null });
  const prevPhoto = () => setLightbox(l => ({ ...l, index: (l.index - 1 + l.photos.length) % l.photos.length }));
  const nextPhoto = () => setLightbox(l => ({ ...l, index: (l.index + 1) % l.photos.length }));

  return (
    <div data-testid="admin-disputes-page">
      <div style={{ marginBottom: 22 }}>
        <div className="c2s-topbar-title">Litiges</div>
        <div className="c2s-topbar-sub">
          {disputes.length} litige{disputes.length !== 1 ? 's' : ''} en cours
        </div>
      </div>

      {disputes.length > 0 && (
        <div className="detail-banner detail-banner--red" style={{ marginBottom: 18 }} data-testid="disputes-alert">
          <div className="detail-banner-icon"><AlertCircle size={20} /></div>
          <div className="detail-banner-body">
            <div className="detail-banner-title">Action requise</div>
            <div className="detail-banner-text">{disputes.length} litige{disputes.length > 1 ? 's' : ''} en attente d'arbitrage. Délai conseillé : 72h.</div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="skeleton" style={{ height: 240, borderRadius: 14 }} />
      ) : disputes.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon"><ShieldCheck size={48} /></div>
          <div className="empty-state-title">Aucun litige en cours</div>
          <div className="empty-state-sub">Toutes les missions sont en bonne voie.</div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {disputes.map(d => {
            const cat = CATEGORY_LABELS[d.dispute?.category] || CATEGORY_LABELS.other;
            const openedBy = d.dispute?.opened_by || d.disputed_by;
            const isShipperReporter = openedBy === d.shipper_id;
            const photos = d.dispute?.photos || [];

            return (
              <div key={d.id} className="dispute-admin-card" data-testid={`dispute-card-${d.id}`}>
                {/* Header */}
                <div className="dispute-admin-head">
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 6 }}>
                      <span className={`mlc-badge mlc-badge--${cat.tone}`}>
                        <AlertTriangle size={11} /> {cat.l}
                      </span>
                      <span style={{ fontSize: 11, color: '#6B7280', fontFamily: 'var(--f-body)' }}>
                        Mission <span style={{ fontFamily: 'monospace', fontWeight: 600 }}>#{(d.id || '').slice(-8).toUpperCase()}</span>
                      </span>
                      <span style={{ fontSize: 11, color: '#6B7280' }}>
                        · Ouvert {fmtDateTime(d.dispute?.opened_at || d.updated_at)}
                      </span>
                    </div>
                    <div style={{ fontFamily: 'var(--f-display)', fontSize: 17, fontWeight: 600, color: '#111827', letterSpacing: '-0.01em' }}>
                      {d.shipment_info?.departure_city || '?'} → {d.shipment_info?.arrival_city || '?'}
                    </div>
                    {d.shipment_info?.title && (
                      <div style={{ fontSize: 12, color: '#6B7280', marginTop: 2 }}>{d.shipment_info.title}</div>
                    )}
                  </div>
                  <div className="detail-price" style={{ minWidth: 90, padding: '6px 12px' }}>
                    <div className="detail-price-amount" style={{ fontSize: 20 }}>{(d.contracted_price || 0).toFixed(0)}<span className="detail-price-currency" style={{ fontSize: 13 }}>{ccySym(d.currency || 'EUR')}</span></div>
                    <div className="detail-price-label">En séquestre</div>
                  </div>
                </div>

                {/* Parties */}
                <div className="dispute-admin-parties">
                  <div className={`dispute-party${isShipperReporter ? ' is-reporter' : ''}`} data-testid={`party-shipper-${d.id}`}>
                    <div className="dispute-party-role">
                      Expéditeur {isShipperReporter && <span className="dispute-party-flag">A signalé</span>}
                    </div>
                    <div className="dispute-party-name">{d.shipper?.name || 'Inconnu'}</div>
                  </div>
                  <div className={`dispute-party${!isShipperReporter ? ' is-reporter' : ''}`} data-testid={`party-carrier-${d.id}`}>
                    <div className="dispute-party-role">
                      Transporteur {!isShipperReporter && <span className="dispute-party-flag">A signalé</span>}
                    </div>
                    <div className="dispute-party-name">{d.carrier?.name || 'Inconnu'}</div>
                  </div>
                </div>

                {/* Message */}
                <div className="dispute-admin-msg">
                  <div className="dispute-admin-msg-label">Description du problème</div>
                  <div className="dispute-admin-msg-body">
                    {d.dispute?.message || d.dispute_message || '(Pas de description)'}
                  </div>
                </div>

                {/* Evidence photos */}
                {photos.length > 0 && (
                  <div className="dispute-admin-msg">
                    <div className="dispute-admin-msg-label" style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                      <ImageIcon size={11} /> Preuves photos ({photos.length})
                    </div>
                    <div className="dispute-admin-photos">
                      {photos.map((p, i) => (
                        <button key={i} type="button" onClick={() => openLightbox(photos, i)} aria-label={`Voir preuve ${i+1}`} data-testid={`dispute-photo-${d.id}-${i}`}>
                          <img src={p} alt={`preuve-${i+1}`} loading="lazy" />
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div className="dispute-admin-actions">
                  <button
                    type="button"
                    className="mlc-btn mlc-btn--ghost"
                    onClick={() => navigate(`/app/missions/${d.id}`)}
                    data-testid={`btn-view-mission-${d.id}`}
                  >
                    <Eye size={14} /> Voir la mission
                  </button>
                  <div style={{ flex: 1 }} />
                  <button
                    type="button"
                    className="mlc-btn"
                    style={{ background: '#B91C1C', color: '#fff' }}
                    onClick={() => setConfirmDlg({ decision: 'refund', id: d.id })}
                    disabled={acting === `${d.id}-refund`}
                    data-testid={`btn-resolve-refund-${d.id}`}
                  >
                    <ShieldX size={14} /> {acting === `${d.id}-refund` ? 'En cours…' : 'Rembourser expéditeur'}
                  </button>
                  <button
                    type="button"
                    className="mlc-btn mlc-btn--primary"
                    onClick={() => setConfirmDlg({ decision: 'release', id: d.id })}
                    disabled={acting === `${d.id}-release`}
                    data-testid={`btn-resolve-release-${d.id}`}
                  >
                    <ShieldCheck size={14} /> {acting === `${d.id}-release` ? 'En cours…' : 'Libérer au transporteur'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <PhotoLightbox
        photos={lightbox.photos}
        index={lightbox.index}
        onClose={closeLightbox}
        onPrev={prevPhoto}
        onNext={nextPhoto}
      />

      <ConfirmDialog
        open={confirmDlg?.decision === 'refund'}
        onClose={() => setConfirmDlg(null)}
        onConfirm={doResolve}
        variant="destructive"
        icon="ban"
        title="Rembourser l'expéditeur ?"
        subtitle="Le montant en séquestre sera restitué à l'expéditeur. Cette action est irréversible."
        confirmLabel="Confirmer le remboursement"
      />
      <ConfirmDialog
        open={confirmDlg?.decision === 'release'}
        onClose={() => setConfirmDlg(null)}
        onConfirm={doResolve}
        variant="financial"
        icon="coin"
        title="Libérer les fonds au transporteur ?"
        subtitle="Le montant en séquestre sera transféré au transporteur. Cette action est irréversible."
        confirmLabel="Confirmer la libération"
      />
    </div>
  );
}
